//#region src/dp-version-floor.ts
/**
* The data-plane platform version floor (2026-10-18-dp-version-floor, decisions 2 and 3;
* 2026-10-19-dp-floor-recovery-refusal; 2026-10-14-app-pools-runtime-deletion R-093).
*
* The CP refuses a data-plane platform version below the floor at these doors:
* - release-engine `PlatformChannelsService.pinVersion` (`POST /v1/platform/channels/:id/pin`);
* - release-engine `PlatformChannelsService.advance` (`POST /v1/platform/channels/:id/advance`);
* - provisioning-svc `ProvisioningService.executeUpgrade` (`POST /v1/data-planes/:id/execute-upgrade`);
* - provisioning-svc `ProvisioningService.forceUpgrade` (`POST /v1/data-planes/:id/force-upgrade`);
* - provisioning-svc `ProvisioningService.reprovision` (`POST /v1/data-planes/:id/reprovision`);
* - provisioning-svc `ProvisioningService.retry` and `resume` (`POST /v1/data-planes/:id/retry`,
*   `POST /v1/data-planes/:id/resume`), on the saved `context.spec.imageTag`. A failure in a
*   destroy state (`DESTROY_FAILED_IN_STATES`) and the `rotate` arm take no check;
* - provisioning-svc `ProvisioningService.restore` (`POST /v1/data-planes/:id/restore`), on the
*   saved `context.spec.imageTag`.
*
* Each door answers 400 before its first write. The floor is DP 1.32.296, the R2 release of
* 2026-10-14-app-pools-runtime-deletion (R-093). R3 drops `auth.machine_principals.instance_id`,
* `auth.instance_registry`, `work.work_runs` and `work.work_schedules` on the DP. It also drops two
* tables and five columns of the `release` schema on the CP. Every DP release below 1.32.296 runs a
* core-api that still selects `auth.machine_principals.instance_id`. Each machine-principal call of
* that core-api answers 500 after the drop. The floor refuses each such label, the R1 release
* 1.32.292 included, so no door moves a data plane back below R2. The R3 CP build carries this
* value and the CP drop migration together.
*
* Producer: this package. Consumers: release-engine and provisioning-svc.
*/
/**
* The lowest data-plane platform version that the CP accepts as a pin, an advance or an upgrade
* target. A plain `MAJOR.MINOR.PATCH` with no pre-release and no build part.
*
* This constant is the only copy of the floor value in the tree. R2 of
* 2026-10-14-app-pools-runtime-deletion removed the `zion-ops app r2-readiness` verb and its own
* copy of the value (R-081 item 5).
*/
const DP_PLATFORM_VERSION_FLOOR = "1.32.296";
/**
* The longest data-plane platform version string that the CP accepts, in characters.
*
* Consumers:
* - provisioning-svc `executeUpgradeSchema` bounds `targetVersion` with it (`.max()`), at
*   `POST /v1/data-planes/:id/execute-upgrade` and `POST /v1/data-planes/:id/force-upgrade`.
* - {@link isAtOrAboveDpVersionFloor} answers `false` for a longer input and runs no pattern on it.
* - {@link dpVersionFloorRefusal} names this bound in its semver text and echoes at most this
*   many characters of a refused version.
*
* The bound is a plain payload bound. It keeps an absurd string out of logs, errors and the
* database. It is no ReDoS mitigation: {@link DP_PLATFORM_VERSION_PATTERN} is linear on
* adversarial input (200 007 characters in 1.45 ms; 0.015 ms at 2 kB, 0.13 ms at 20 kB).
*
* In a zod schema the bound does not short-circuit the pattern check. Measured on zod 4.4.3 by a
* count of `RegExp.prototype.test` calls for the pattern on an over-long input:
* `.max().regex()` makes 1 call, and `.regex().max()` makes 1 call. Declaration order is
* irrelevant. Only `z.string().max(n).pipe(z.string().regex(p))` makes 0 calls, and
* `executeUpgradeSchema` does not use that pipe, because the linear pattern needs no guard.
*/
const DP_PLATFORM_VERSION_MAX_LENGTH = 64;
/**
* The official semver.org grammar, anchored: `MAJOR.MINOR.PATCH`, an optional `-prerelease` part
* and an optional `+build` part.
*
* Groups: 1 to 3 hold the core parts, 4 the pre-release part, 5 the build part. The pattern has
* no `g` and no `y` flag, so `exec` and `test` keep no state between calls.
*
* Consumers: provisioning-svc `executeUpgradeSchema` (`.regex()` on `targetVersion`, so the
* pattern source appears in the provisioning-svc OpenAPI document) and
* {@link isAtOrAboveDpVersionFloor} (groups 1 to 4).
*
* The pattern is stricter than "looks version-ish", on purpose. It refuses the values that reach
* a version field by mistake rather than by malice: `latest`, `v1.2.3`, a git SHA, a branch name,
* `1.2`, `01.2.3`. `bin/release-dp` registers the value from `cloud/dp/platform.json` (for
* example `1.32.81`), so every real platform version is exact semver and the pattern excludes
* nothing legitimate.
*
* A shape check is the cheap half of an upgrade-target check, and it cannot be the whole check:
* `9.9.9` is flawless semver and does not exist. The registry check in provisioning-svc
* `platform-version-registry.ts` is the half that closes #1204. This pattern only stops the
* obviously wrong value at the boundary.
*/
const DP_PLATFORM_VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;
/** The three core parts of {@link DP_PLATFORM_VERSION_FLOOR}, as exact integers. */
const FLOOR_CORE = DP_PLATFORM_VERSION_FLOOR.split(".").map((part) => BigInt(part));
/**
* Tells whether a data-plane platform version is at or above {@link DP_PLATFORM_VERSION_FLOOR}.
*
* Callers: {@link dpVersionFloorRefusal}, which the CP doors named in the file header call.
* Each door refuses with 400 when this returns `false`, and writes nothing.
*
* Input: any string, from an unvalidated request body or from a DTO that already checked the
* grammar. The function never throws and has no side effects.
*
* Output per input class:
* - longer than {@link DP_PLATFORM_VERSION_MAX_LENGTH} (64) characters: `false`, before any
*   pattern runs;
* - not {@link DP_PLATFORM_VERSION_PATTERN} (`""`, `latest`, `v1.32.296`, `1.32`, `1.32.296.1`,
*   `01.32.296`):
*   `false`, so each CP door refuses a string that it cannot order;
* - a core triple above the floor core: `true`, with or without a pre-release part
*   (`1.32.297-rc.1` is `true`);
* - a core triple below the floor core: `false` (`1.32.295` and the R1 release `1.32.292` are
*   `false`);
* - the floor core: `true` with no pre-release part, `false` with one (`1.32.296-rc.1` is
*   `false`, because semver orders a pre-release below its release).
*
* The build part never changes the answer (`1.32.296+build.5` is `true`). The function compares
* the core parts one at a time, major first, as exact integers (`BigInt`). `BigInt` keeps a part
* above `Number.MAX_SAFE_INTEGER` exact, and a large lower part never carries into a higher one.
*/
function isAtOrAboveDpVersionFloor(version) {
	if (version.length > 64) return false;
	const match = DP_PLATFORM_VERSION_PATTERN.exec(version);
	if (match === null) return false;
	const core = [
		match[1],
		match[2],
		match[3]
	].map((part) => BigInt(part));
	for (let index = 0; index < FLOOR_CORE.length; index += 1) {
		const part = core[index];
		const floorPart = FLOOR_CORE[index];
		if (part !== floorPart) return part > floorPart;
	}
	return match[4] === void 0;
}
/**
* The 400 text of a data-plane platform version that the CP refuses as a pin, an advance or an
* upgrade target (2026-10-18-dp-version-floor, decision 5). One builder serves every door that the
* file header names, so they answer the same text for the same value.
*
* Callers: release-engine `PlatformChannelsService.pinVersion` and `advance`, provisioning-svc
* `ProvisioningService.executeUpgrade`, `forceUpgrade`, `reprovision`, `restore` and the private
* `resumeOperation` of `retry` and `resume`. Each door throws `BadRequestException` with this text,
* or with this text after a door prefix such as `Cannot restore data plane <id>: `, when this
* returns a string, and writes nothing.
*
* Input: any string, from an unvalidated request body or from a DTO that already checked the
* grammar. The function never throws and has no side effects.
*
* Output per input class, in this order:
* - {@link isAtOrAboveDpVersionFloor} answers `true`: `null`, and the door goes on;
* - longer than {@link DP_PLATFORM_VERSION_MAX_LENGTH} (64) characters: the semver text, which
*   echoes the first 64 characters and then `…`, before any pattern runs:
*   `Platform version "<first 64>…" is not a semver platform version of at most 64 characters (e.g. 1.32.296).`;
* - not {@link DP_PLATFORM_VERSION_PATTERN} (`latest`, `v1.40.0`): the semver text, which echoes
*   the whole value;
* - any other value (a parsable version below the floor, `1.32.295` or `1.32.296-rc.1`): the floor
*   text, which echoes the whole value:
*   `Platform version "1.32.295" is below the data-plane version floor 1.32.296.`
*
* The text names the rejected value and the rule, and lists no registry row. The echo never holds
* more than 64 characters of the value.
*/
function dpVersionFloorRefusal(version) {
	if (isAtOrAboveDpVersionFloor(version)) return null;
	if (version.length > 64) return notSemverText(`${version.slice(0, 64)}…`);
	if (!DP_PLATFORM_VERSION_PATTERN.test(version)) return notSemverText(version);
	return `Platform version "${version}" is below the data-plane version floor ${DP_PLATFORM_VERSION_FLOOR}.`;
}
/** The semver text of {@link dpVersionFloorRefusal}, for an echo that is already bounded. */
function notSemverText(echoed) {
	return `Platform version "${echoed}" is not a semver platform version of at most 64 characters (e.g. ${DP_PLATFORM_VERSION_FLOOR}).`;
}
//#endregion
export { DP_PLATFORM_VERSION_FLOOR, DP_PLATFORM_VERSION_MAX_LENGTH, DP_PLATFORM_VERSION_PATTERN, dpVersionFloorRefusal, isAtOrAboveDpVersionFloor };

//# sourceMappingURL=dp-version-floor.js.map